import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { HelpButton } from '../components/HelpButton.js';
import { NotFound, PageTitle } from '../components/Layouts.js';
import {
  AI,
  BeforeAfterRow,
  CLASSIC,
  Legend,
  PaybackChart,
  useFormat,
  hi,
  lo,
  useModernization,
} from '../components/modernization.js';
import { Card, LoadError, Loading } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { BackButton } from '../components/BackButton.js';

/** Who can do a task: the owner with a plugin, a developer, the host, or we do it ourselves. */
const WHO: Record<string, 'you' | 'developer' | 'host' | 'us' | 'designer'> = {
  images: 'you',
  lazy_loading: 'you',
  unused_code: 'developer',
  cache_compression: 'host',
  verify: 'us',
  cms_update: 'developer',
  php_upgrade: 'host',
  security_headers: 'developer',
  accessibility: 'developer',
  backup_test: 'developer',
  local_business: 'you',
  titles_descriptions: 'you',
  open_graph: 'developer',
  relaunch_design: 'designer',
  relaunch_build: 'developer',
  content_migration: 'developer',
  redirects_seo: 'developer',
  relaunch_testing: 'developer',
};

/** One modernization package (T-39): now, meaning, steps, gain, effort, cost and payback. */
export function ModernizationPackage() {
  const { siteId = '', packageKey = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const query = useModernization(siteId);

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const m = query.data;
  const p = m.packages.find((x) => x.key === packageKey);
  if (!p) return <NotFound />;
  const maxTask = Math.max(...p.tasks.map((x) => x.classicHours));
  const e = p.effect;

  return (
    <>
      <p className="mb-4">
        <BackButton to={`/sites/${siteId}/modernization`}>{t('modern.back')}</BackButton>
      </p>
      <PageTitle
        actions={
          <div className="grid grid-cols-2 gap-3">
            <Card className="flex flex-col py-3">
              <span className="text-sm text-slate-700">{t('modern.costAi')}</span>
              <span className="text-2xl font-bold tabular-nums" style={{ color: AI }}>
                {f.euroRange(p.cost.ai)}
              </span>
              <span className="text-sm text-slate-700 tabular-nums">
                {t('modern.classicCost', { value: f.euroRange(p.cost.classic) })}
              </span>
            </Card>
            <Card className="flex flex-col py-3">
              <span className="text-sm text-slate-700">{t('modern.payback.label')}</span>
              <span className="text-2xl font-bold text-green-800">
                {p.paybackMonths
                  ? f.months(p.paybackMonths)
                  : p.uplift
                    ? '—'
                    : t(`modern.packages.${p.key}.paybackNote`)}
              </span>
            </Card>
          </div>
        }
      >
        {t(`modern.packages.${p.key}.title`)}
      </PageTitle>
      <p className="-mt-4 mb-6 max-w-prose text-slate-700">
        {t(`modern.packages.${p.key}.summary`)}
      </p>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.now')}</h2>
          {p.findings.length > 0 ? (
            <ul className="list-disc pl-5">
              {p.findings.map((x) => (
                <li key={x.ruleCode}>{x.title}</li>
              ))}
            </ul>
          ) : (
            <p>{t(`modern.packages.${p.key}.now`)}</p>
          )}
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.means')}</h2>
          <p>{t(`modern.packages.${p.key}.means`)}</p>
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.todo')}</h2>
          <ol className="flex list-decimal flex-col gap-2 pl-5">
            {p.tasks.map((task) => (
              <li key={task.key}>
                {t(`modern.tasks.${task.key}`)}{' '}
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-800">
                  {t(`modern.who.${WHO[task.key] ?? 'developer'}`)}
                </span>
              </li>
            ))}
          </ol>
        </Card>
        <Card className="border-green-300">
          <h2 className="mb-2 text-lg font-semibold">{t('area.gain')}</h2>
          <p>{t(`modern.packages.${p.key}.gain`)}</p>
        </Card>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t('modern.accessTitle')}</h2>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {t(`modern.packages.${p.key}.access`)
              .split('\n')
              .map((line) => (
                <li key={line}>{line}</li>
              ))}
          </ul>
          <p className="text-sm text-slate-700">{t('modern.consent')}</p>
        </Card>
        <Card className="flex flex-col gap-2 border-violet-200 bg-violet-50">
          <h2 className="text-lg font-semibold text-violet-950">{t('modern.aiHelpTitle')}</h2>
          <p className="text-violet-950">{t(`modern.packages.${p.key}.aiHelp`)}</p>
        </Card>
      </div>

      {e.lcpMs || e.pageBytes || e.score ? (
        <Card className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-semibold">{t('modern.effectTitle')}</h2>
            <span className="text-sm text-slate-700">{t('modern.effectHint')}</span>
          </div>
          {e.lcpMs ? (
            <BeforeAfterRow
              label={t('modern.facts.lcp')}
              now={lo(e.lcpMs)}
              after={hi(e.lcpMs)}
              format={f.seconds}
            />
          ) : null}
          {e.pageBytes ? (
            <BeforeAfterRow
              label={t('modern.facts.weight')}
              now={lo(e.pageBytes)}
              after={hi(e.pageBytes)}
              format={f.megabytes}
            />
          ) : null}
          {e.score ? (
            <BeforeAfterRow
              label={t('modern.facts.score')}
              now={lo(e.score)}
              after={hi(e.score)}
              format={(v) => `${v} / 100`}
              higherIsBetter
            />
          ) : null}
        </Card>
      ) : null}

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold">{t('modern.hoursTitle')}</h2>
          <div className="grid gap-x-3 gap-y-3 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-center">
            {p.tasks.map((task) => (
              <div key={task.key} className="contents">
                <span className="text-sm">{t(`modern.tasks.${task.key}`)}</span>
                <div className="flex flex-col gap-1">
                  {(
                    [
                      [task.classicHours, CLASSIC],
                      [task.aiHours, AI],
                    ] as const
                  ).map(([h, color]) => (
                    <div key={color} className="flex items-center gap-2">
                      <span className="h-2.5 flex-1 rounded bg-slate-100">
                        <span
                          className="block h-2.5 rounded"
                          style={{ width: `${(h / maxTask) * 100}%`, background: color }}
                        />
                      </span>
                      <span className="w-10 shrink-0 text-sm text-slate-700 tabular-nums">
                        {f.num(h)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3 text-sm">
            <Legend />
            <span className="text-slate-700 tabular-nums">
              {t('modern.rate', { value: f.euro(m.figures.hourlyRate) })}
            </span>
          </div>
          <p className="rounded-md bg-violet-50 p-3 text-sm text-violet-950">
            {t('modern.how.ai')}
          </p>
        </Card>

        <Card className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold">{t('modern.payback.title')}</h2>
          {p.monthlyGain && p.uplift ? (
            <>
              <PaybackChart cost={hi(p.cost.ai)} gain={p.monthlyGain} />
              <div className="flex flex-wrap gap-4 text-sm text-slate-800">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="w-6 border-t-[3px] border-green-700" />
                  {t('modern.payback.high', {
                    share: f.percent(hi(p.uplift)),
                    value: f.euro(hi(p.monthlyGain)),
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="w-6 border-t-[3px] border-dashed border-green-700"
                  />
                  {t('modern.payback.low', {
                    share: f.percent(lo(p.uplift)),
                    value: f.euro(lo(p.monthlyGain)),
                  })}
                </span>
              </div>
              <p className="text-sm text-slate-700">{t('modern.payback.explain')}</p>
            </>
          ) : p.uplift ? (
            <p>{t('modern.payback.needFiguresLong')}</p>
          ) : (
            <p>{t(`modern.packages.${p.key}.paybackLong`)}</p>
          )}
        </Card>
      </div>

      <Card className="mb-6">
        <h2 className="mb-2 text-lg font-semibold">{t('modern.sources.title')}</h2>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-slate-800">
          <li>{t('modern.sources.deloitte')}</li>
          <li>{t('modern.sources.google')}</li>
          <li>{t('modern.sources.hours')}</li>
        </ul>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        {p.service ? <HelpButton siteId={siteId} service={p.service} /> : null}
        <Link
          to={`/sites/${siteId}/findings`}
          className="inline-flex min-h-11 items-center rounded-md border border-slate-300 px-4 font-semibold text-slate-900 no-underline"
        >
          {t('modern.diy')}
        </Link>
      </div>
      <p className="mt-4 text-sm text-slate-700">{t('modern.disclaimer')}</p>
    </>
  );
}
